import { Box, Button, LinearProgress, Stack, TextField, Typography } from "@mui/material"
import { useState, useEffect, useRef } from "react"
import { useSnackbar } from "notistack"
import { Repeat } from "lib/@components/repeat"
import { If, Else, Switch, Case } from "lib/@components/switch"
import { getContentLanguages } from "routes/schedule/plugins/translation/controller/get-content-languages"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import {
    batchTranslateCustomSchedules,
    cancelBatchTranslation,
    getBatchTranslationProgress,
} from "routes/schedule/plugins/translation/controller/batch-translate-schedules"
import { LanguageOption } from "./language-option"

export function BatchTranslateCustomPanel() {
    const { enqueueSnackbar } = useSnackbar()
    const rawLanguages = getContentLanguages.useResults(withInitialValue(IS_LOADING))
    const languages = rawLanguages === IS_LOADING ? [] : rawLanguages ?? []
    const nonEnglish = languages.filter(isNotEnglish)
    const [selectedLanguage, setSelectedLanguage] = useState("")
    const [jobId, setJobId] = useState(null)
    const [progress, setProgress] = useState(null)
    const [polling, setPolling] = useState(false)
    const [starting, setStarting] = useState(false)
    const [cancelRequested, setCancelRequested] = useState(false)
    const timerRef = useRef(null)

    useEffect(pollProgress, [jobId, polling])
    useEffect(cleanupTimer, [])

    const hasActiveJob = Boolean(jobId) && polling && !["complete", "error", "cancelled"].includes(progress?.status)
    const isBusy = starting || hasActiveJob
    const startButtonLabel = getStartButtonLabel()
    const pct = progress?.total ? Math.round(((progress.completed + progress.failed) / progress.total) * 100) : 0

    return (
        <Box p={3}>
            <Typography variant="h6" gutterBottom>
                Batch Translate Custom Schedules
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Translate your custom schedules into a selected language. This pre-populates the translation cache so
                users see instant translations when browsing custom schedules.
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <TextField
                    select
                    label="Target Language"
                    value={selectedLanguage}
                    onChange={handleLanguageChange}
                    sx={{ minWidth: 240 }}
                    size="small"
                    disabled={isBusy}
                >
                    <Repeat list={nonEnglish} item={<LanguageOption />} />
                </TextField>
                <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={handleStart} disabled={!selectedLanguage || isBusy}>
                        {startButtonLabel}
                    </Button>
                    <If value={hasActiveJob}>
                        <Button variant="outlined" color="error" onClick={handleCancel} disabled={cancelRequested}>
                            <If value={cancelRequested}>
                                Cancelling...
                                <Else>Cancel</Else>
                            </If>
                        </Button>
                    </If>
                </Stack>
            </Stack>

            <If value={progress}>
                <Box sx={{ maxWidth: 500 }}>
                    <LinearProgress variant="determinate" value={pct} sx={{ mb: 1, height: 8, borderRadius: 4 }} />
                    <Typography variant="body2" color="text.secondary">
                        <Switch value={progress?.status}>
                            <Case when="cancelled">
                                Translation cancelled. {progress?.completed ?? 0} translated before cancellation.
                                <If value={progress?.failed}> ({progress?.failed} failed)</If>
                            </Case>
                            <Case when="translating">
                                Translating {progress?.completed ?? 0} of {progress?.total ?? 0}
                                <If value={progress?.failed}> ({progress?.failed} failed)</If>
                            </Case>
                            <Case when="complete">
                                Complete — {progress?.completed} translated
                                <If value={progress?.failed}>, {progress?.failed} failed</If>
                            </Case>
                            <Case when="error">
                                Translation encountered an error. {progress?.completed ?? 0} completed before failure.
                            </Case>
                            <Case when="counting">Counting schedules...</Case>
                        </Switch>
                    </Typography>
                </Box>
            </If>
        </Box>
    )

    function isNotEnglish(lang) {
        return lang.code !== "en"
    }

    function handleLanguageChange(e) {
        setSelectedLanguage(e.target.value)
    }

    async function handleStart() {
        if (!selectedLanguage || isBusy) return
        try {
            setStarting(true)
            const id = await batchTranslateCustomSchedules({ language: selectedLanguage })
            setJobId(id)
            setProgress({
                total: 0,
                completed: 0,
                failed: 0,
                status: "counting",
                startedAt: new Date().toISOString(),
            })
            setCancelRequested(false)
            setPolling(true)
        } catch (e) {
            console.error("Failed to start batch translation:", e)
            enqueueSnackbar(e?.message ?? "Failed to start translation", { variant: "error" })
        } finally {
            setStarting(false)
        }
    }

    async function handleCancel() {
        if (!jobId || !hasActiveJob || cancelRequested) return
        try {
            setCancelRequested(true)
            await cancelBatchTranslation({ jobId })
        } catch (e) {
            setCancelRequested(false)
            console.error("Failed to cancel batch translation:", e)
            enqueueSnackbar(e?.message ?? "Failed to cancel translation", { variant: "error" })
        }
    }

    function pollProgress() {
        clearInterval(timerRef.current)
        if (jobId && polling) {
            timerRef.current = setInterval(fetchProgress, 3000)
            void fetchProgress()
        }
        return clearTimer

        async function fetchProgress() {
            try {
                const p = await getBatchTranslationProgress({ jobId })
                if (p) {
                    setProgress(p)
                    if (p.status === "complete" || p.status === "error" || p.status === "cancelled") {
                        setPolling(false)
                        setCancelRequested(false)
                        clearInterval(timerRef.current)
                    }
                }
            } catch (e) {
                console.error("Failed to fetch progress:", e)
            }
        }
    }

    function cleanupTimer() {
        return clearTimer
    }

    function clearTimer() {
        clearInterval(timerRef.current)
    }

    function getStartButtonLabel() {
        if (starting) {
            return "Starting..."
        }
        if (hasActiveJob) {
            return "Translating..."
        }
        return "Start Translation"
    }
}
